import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

function AdditionalSettingCheckbox({ selected, formik, onClose }) {
  const { t } = useTranslation();
  const values = formik.values.additional_items;
  const [initialData, setInitialData] = useState(JSON.parse(JSON.stringify(values)));

  const selectedCount = initialData.filter((item) => item[selected.value] === true).length;

  const handleToggle = (idx) => {
    let arr = [...initialData];
    arr[idx][selected.value] = !arr[idx][selected.value];
    setInitialData(arr);
  };

  const handleSelectAll = () => {
    let arr = [...initialData];
    arr.forEach((item) => (item[selected.value] = true));
    setInitialData(arr);
  };

  const handleClearAll = () => {
    let arr = [...initialData];
    arr.forEach((item) => (item[selected.value] = false));
    setInitialData(arr);
  };

  return (
    <div className="p-6 bg-white rounded-sm md:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-[#f5272b] to-red-600 rounded-xl flex items-center justify-center shadow-lg">
            <i className="text-white fa-solid fa-calendar-check"></i>
          </div>
          <h3 className="text-xl font-bold md:text-2xl text-slate-900">
            {t("setting_date_use")} {selected.title}
          </h3>
        </div>
        <p className="text-sm text-slate-600 ml-13">{t("select_dates_apply_item")}</p>
      </div>

      {/* Summary & Actions */}
      <div className="p-4 mb-6 border bg-gradient-to-r from-slate-50 to-white rounded-xl border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-white rounded-lg shadow-sm">
              <span className="text-xl font-bold text-[#f5272b]">{selectedCount}</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {selectedCount} {selectedCount === 1 ? "day" : "days"} selected
              </p>
              <p className="text-xs text-slate-600">
                {t("out_of")} {initialData.length} {t("total_day")}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSelectAll}
              className="px-4 py-2 text-sm font-semibold text-white transition-all duration-300 bg-green-500 rounded-lg hover:bg-green-600"
            >
              <i className="mr-2 fa-solid fa-check-double"></i>
              {t("select_all")}
            </button>
            <button
              type="button"
              onClick={handleClearAll}
              className="px-4 py-2 text-sm font-semibold transition-all duration-300 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700"
            >
              <i className="mr-2 fa-solid fa-xmark"></i>
              {t("clear_all")}
            </button>
          </div>
        </div>
      </div>

      {/* Date List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto mb-6 pr-2">
        {initialData?.map((item, idx) => {
          const isChecked = item[selected.value];
          const dateFormatted = moment(item.date).format("ddd, DD MMMM YYYY");

          return (
            <button
              type="button"
              key={idx}
              onClick={() => handleToggle(idx)}
              className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-300 ${
                isChecked
                  ? "bg-gradient-to-r from-green-50 to-green-100 border-green-300 shadow-md"
                  : "bg-white border-slate-200 hover:border-[#f5272b]/30 hover:shadow-sm"
              }`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                    isChecked ? "bg-green-500 text-white shadow-lg shadow-green-500/30" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {idx + 1}
                </div>
                <div className="text-left">
                  <p className={`font-semibold ${isChecked ? "text-green-900" : "text-slate-900"}`}>{dateFormatted}</p>
                  {isChecked && (
                    <p className="text-xs text-green-600 mt-0.5">
                      <i className="mr-1 fa-solid fa-check-circle"></i>
                      {t("item_will_be_applied")}
                    </p>
                  )}
                </div>
              </div>

              {/* Checkbox */}
              <div
                className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all duration-300 ${
                  isChecked ? "bg-green-500 border-green-500" : "bg-white border-slate-300"
                }`}
              >
                {isChecked && <i className="text-sm text-white fa-solid fa-check"></i>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer Buttons */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={() => {
            setInitialData([]);
            onClose();
          }}
          className="flex-1 px-6 py-3 font-semibold transition-all duration-300 border bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border-slate-200"
        >
          <i className="mr-2 fa-solid fa-xmark"></i>
          {t("cancel")}
        </button>
        <button
          type="button"
          onClick={() => {
            formik.setFieldValue("additional_items", initialData);
            onClose();
          }}
          className="flex-1 px-6 py-3 bg-[#f5272b] hover:bg-red-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-[#f5272b]/30"
        >
          <i className="mr-2 fa-solid fa-check"></i>
          {t("save")} ({selectedCount})
        </button>
      </div>
    </div>
  );
}

export default AdditionalSettingCheckbox;
